/**
 * "Move to live now" — the three things confirming can do.
 * For Media Devoted | Created By Ciro Autiero
 *
 * `raise the cap` is ONE user action, so it does the whole job: save the higher
 * cap, then promote. The config PUT happens here rather than inside the promote
 * endpoint on purpose — config writes need `flows.edit_settings` while promote
 * needs `flows.edit_weights`, so bundling them server-side would silently
 * escalate. From the client the user's own token decides, and a missing
 * permission surfaces as a visible 403 instead.
 */

import type { CapacityRule, PromotePlanResult } from '@trafficpulse/shared';

import { getApiErrorMessage } from '@/lib/api-error';
import { useToast } from '@/providers/ToastProvider';
import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';

import { useUpsertFlowOptimizerConfig } from '../../../hooks/useFlowOptimizerConfig';
import {
  usePromotePlan,
  usePromoteToLive,
} from '../../../hooks/usePromoteToLive';
import { raiseCapFor } from './cap-raise';

export interface PromoteActionsInput {
  campaignId: string;
  provider: QueueProvider;
  capacityRules: CapacityRule[];
  onCapacityRulesChange?: (next: CapacityRule[]) => void;
  onDone: () => void;
}

export function usePromoteActions(input: PromoteActionsInput) {
  const toast = useToast();
  const plan = usePromotePlan(input.campaignId, input.provider);
  const promote = usePromoteToLive(input.campaignId, input.provider);
  const upsert = useUpsertFlowOptimizerConfig(input.campaignId, input.provider);

  const live = async (p: PromotePlanResult, victimBankId?: string) => {
    try {
      const r = await promote.mutateAsync({
        layer: p.layer,
        bankId: p.bankId,
        victimBankId,
      });
      input.onDone();
      const title = r.replaced
        ? `${p.itemName} is live — ${r.replaced.itemName} moved to Waiting`
        : `${p.itemName} is live`;
      const skipped = r.skippedLanders ?? [];
      if (skipped.length > 0) {
        // Partial success: the path IS live, minus the landers the tracker
        // refused — say who was left behind and the tracker's reason why.
        const who = skipped.map((s) => `"${s.landerName}"`).join(', ');
        toast.warning(
          title,
          `Everything passed except ${who} — ${skipped[0].reason}`,
        );
      } else {
        toast.success(title);
      }
    } catch (e) {
      toast.error('Could not move it live', getApiErrorMessage(e));
    }
  };

  /** Save the higher cap, keep the drawer draft in step, then go live. */
  const raiseCapAndLive = async (p: PromotePlanResult) => {
    const max = (p.cap ?? 0) + 1;
    const next = raiseCapFor(input.capacityRules, p.pathScope ?? 'default', max);
    if (!next) {
      toast.error('Could not raise the cap here', 'Edit it on the Rules tab.');
      return;
    }
    try {
      await upsert.mutateAsync({ flowId: p.flowId, capacityRules: next });
    } catch (e) {
      toast.error('Could not save the higher cap', getApiErrorMessage(e));
      return;
    }
    // Without this the drawer's unsaved draft still holds the old cap and the
    // next "Save Config" would quietly put it back.
    input.onCapacityRulesChange?.(next);
    toast.success(`Cap raised to ${max}`);
    await live(p);
  };

  return {
    plan,
    promote,
    live,
    raiseCapAndLive,
    busy: promote.isPending || upsert.isPending,
  };
}
